import type { MyIdentityFieldsFragment } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { useRedirect } from '@mbfab/frontend-common'
import { isSome } from '@mbfab/shared'
import { Button, Input, Layout, List, Result } from 'antd'
import type { AppProps } from 'next/app'
import { Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { ErrorPage } from '../components/ui/ErrorPage'
import { SuspenseWithLoading } from '../components/ui/SuspenseWithLoading'
import { routes } from '../config/routes'
import { AuthenticatedApolloProvider } from '../providers/AuthenticatedApolloProvider'

import { DefaultHead } from './base/DefaultHead'

export const UserSelectLayout = (props: AppProps) => {
  const redirect = useRedirect()
  // ユーザー選択画面のパスじゃなければリダイレクトする
  useEffect(() => {
    if (props.router.pathname !== '/') {
      redirect('/')
    }
  }, [props.router.pathname])

  return (
    <Suspense>
      <AuthenticatedApolloProvider>
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <SuspenseWithLoading>
            <>
              <Suspense>
                <LogicBodyWrapper {...props} />
              </Suspense>
            </>
          </SuspenseWithLoading>
        </ErrorBoundary>
      </AuthenticatedApolloProvider>
    </Suspense>
  )
}

function LogicBodyWrapper(appProps: AppProps) {
  const myIdentityQuery = api.useFetchMyIdentityQuery()
  if (isSome(myIdentityQuery.error)) {
    return <ErrorPage error={myIdentityQuery.error} />
  }
  const myIdentity = myIdentityQuery.data?.myIdentity
  if (myIdentity === undefined) {
    return <></>
  }

  return <LogicBody {...appProps} myIdentity={myIdentity} />
}

function LogicBody({ myIdentity }: AppProps & { myIdentity: MyIdentityFieldsFragment }) {
  const redirect = useRedirect()
  const [searchText, setSearchText] = useState('')

  if (myIdentity.users.length === 0) {
    return (
      <>
        <DefaultHead />
        <Layout style={{ height: '100%' }} className="w-full">
          <Layout.Content className="overflow-visible-print h-full overflow-y-auto pb-16">
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
              <Result
                status="error"
                title="アクセス権限がありません"
                subTitle="現在のアカウントでは、ログインできるテナントが存在しません。管理者に招待を依頼してください。"
                extra={[
                  <Button
                    key="logout"
                    type="primary"
                    onClick={() => {
                      redirect(routes.signOutPath())
                    }}
                  >
                    ログアウト
                  </Button>,
                ]}
              />
            </div>
          </Layout.Content>
        </Layout>
      </>
    )
  }

  if (myIdentity.users.length === 1) {
    redirect(routes.topPath(myIdentity.users[0]!.tenant.id))
    return <></>
  }

  // 検索フィルタリング
  const filteredUsers = myIdentity.users.filter((user) => {
    const searchLower = searchText.toLowerCase()
    const tenantName = user.tenant.name.toLowerCase()
    const companyName = user.tenant.companyName?.toLowerCase() || ''

    return tenantName.includes(searchLower) || companyName.includes(searchLower)
  })

  // ハイライト表示用の関数
  const highlightText = (text: string, searchText: string) => {
    if (!searchText) {
      return text
    }

    const regex = new RegExp(`(${searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : (
        part
      ),
    )
  }

  return (
    <>
      <DefaultHead />
      <Layout style={{ height: '100%' }} className="w-full">
        <Layout.Content className="overflow-visible-print h-full overflow-y-auto pb-16">
          <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center text-xl font-bold text-gray-800 mb-6">テナントを選択してください</div>
              {myIdentity.role === 'admin' && (
                <div className="mb-6 text-center">
                  <Button
                    type="link"
                    onClick={() => {
                      redirect(routes.adminTenants())
                    }}
                  >
                    管理者メニューへ
                  </Button>
                </div>
              )}

              {/* 検索ボックス */}
              {myIdentity.users.length > 5 && (
                <div className="mb-6">
                  <Input.Search
                    placeholder="テナント名または会社名で検索"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value)
                    }}
                    size="large"
                    className="max-w-md mx-auto"
                  />
                </div>
              )}

              {/* テナント一覧 */}
              <div className="bg-white rounded-lg shadow-sm">
                <List
                  dataSource={filteredUsers}
                  renderItem={(user) => (
                    <List.Item
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                      onClick={() => {
                        if (user.role === 'member') {
                          return
                        }
                        redirect(routes.topPath(user.tenant.id))
                      }}
                    >
                      <div className="flex items-center w-full py-1 px-6">
                        <div className="text-2xl mr-4">🏢</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 mb-1">
                            {highlightText(user.tenant.name, searchText)}
                          </div>
                          {user.tenant.companyName && user.tenant.companyName.length > 0 && (
                            <div className="text-sm text-gray-600">
                              {highlightText(user.tenant.companyName, searchText)}
                            </div>
                          )}
                        </div>
                        {user.role !== 'member' ? (
                          <div className="text-gray-400">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className="text-gray-400">
                            <span className="ml-2">一般メンバー権限であるため、管理画面にはアクセスできません</span>
                          </div>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
              </div>

              {/* 検索結果が0件の場合 */}
              {filteredUsers.length === 0 && searchText && (
                <div className="text-center text-gray-500 mt-8">検索条件に一致するテナントが見つかりませんでした。</div>
              )}
            </div>
          </div>
        </Layout.Content>
      </Layout>
    </>
  )
}
